import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../storeHooks";
import { aspectRatioClassMap } from "../../utils";
import { CameraParking } from "../CameraParking/CameraParking";
import { usePersistentRecording } from "../Recorder/usePersistentRecording";
import "./CameraViewer.css";
import ParkingGrid from "../ParkingGrid/ParkingGrid";
export const CameraViewer = ({ camera, stream, isCameraLoaded, parkingSensors, smallView, usage, onStream, onStartSegment, }) => {
    const videoElement = useRef(null);
    const wrapperElement = useRef(null);
    const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });
    const [videoBox, setVideoBox] = useState({ left: 0, top: 0, width: 0, height: 0 });
    const camerasRatio = useAppSelector((store) => store.activeCamera.value.ratio);
    const showParkingGrid = useAppSelector((store) => store.activeCamera.value.showParkingGrid);
    // Attach stream to video element
    useEffect(() => {
        const el = videoElement.current;
        if (!el)
            return;
        el.srcObject = stream;
        if (stream)
            el.play().catch(() => { });
    }, [stream]);
    // Notify parent when stream is live (used by small-view in portal CameraView)
    useEffect(() => {
        if (stream && isCameraLoaded && usage) {
            onStream === null || onStream === void 0 ? void 0 : onStream(stream, usage);
        }
    }, [stream, isCameraLoaded]);
    // Recording resume: when a view-switch happens during recording,
    // usePersistentRecording watches pendingResume in the store and calls
    // onStartSegment once the track is live again.
    const { isWaiting } = usePersistentRecording(usage !== null && usage !== void 0 ? usage : "front", isCameraLoaded && !!stream, () => {
        if (stream && usage)
            onStartSegment === null || onStartSegment === void 0 ? void 0 : onStartSegment(stream, usage);
    }, () => {
        console.error(`[CameraViewer] Track "${usage}" timed out for recording resume`);
    });
    // Track video frame size and rendered box for the parking overlays
    useEffect(() => {
        const video = videoElement.current;
        if (!video)
            return;
        const updateFrameSize = () => {
            const width = video.videoWidth;
            const height = video.videoHeight;
            setFrameSize((prev) => prev.width === width && prev.height === height
                ? prev
                : { width, height });
        };
        const updateVideoBox = () => {
            const left = video.offsetLeft;
            const top = video.offsetTop;
            const width = video.offsetWidth;
            const height = video.offsetHeight;
            setVideoBox((prev) => prev.left === left &&
                prev.top === top &&
                prev.width === width &&
                prev.height === height
                ? prev
                : { left, top, width, height });
        };
        video.addEventListener("loadedmetadata", updateFrameSize);
        video.addEventListener("resize", updateFrameSize);
        const resizeObserver = new ResizeObserver(updateVideoBox);
        resizeObserver.observe(video);
        if (wrapperElement.current)
            resizeObserver.observe(wrapperElement.current);
        updateFrameSize();
        updateVideoBox();
        return () => {
            video.removeEventListener("loadedmetadata", updateFrameSize);
            video.removeEventListener("resize", updateFrameSize);
            resizeObserver.disconnect();
        };
    }, []);
    return (_jsxs(_Fragment, { children: [_jsxs("div", { ref: wrapperElement, className: `video-wrapper-item ${camera[camerasRatio]
                    ? aspectRatioClassMap[camera[camerasRatio]]
                    : ""}`, children: [_jsx("video", { className: camera[camerasRatio]
                            ? aspectRatioClassMap[camera[camerasRatio]]
                            : "", ref: videoElement, autoPlay: true, playsInline: true, muted: true, controls: false, style: { width: "100%" } }), !smallView &&
                        showParkingGrid &&
                        !!camera.hFoV &&
                        !!camera.vFoV &&
                        frameSize.width > 0 &&
                        frameSize.height > 0 && (_jsx(ParkingGrid, { heightMeters: 1.5, pitchDeg: 15, fovHorizontalDeg: camera.hFoV, fovVerticalDeg: camera.vFoV, frameWidth: frameSize.width, frameHeight: frameSize.height, lengthMeters: 5, widthMeters: 2, stepMeters: 2, tickMeters: 0.4, showLabels: true, 
                        // Как у <video> по умолчанию (object-fit: contain).
                        preserveAspectRatio: "xMidYMid meet", style: {
                            position: "absolute",
                            left: videoBox.left,
                            top: videoBox.top,
                            width: videoBox.width,
                            height: videoBox.height,
                            pointerEvents: "none",
                        } })), !smallView && videoBox.width > 0 && parkingSensors && (_jsx(CameraParking, { sensors: parkingSensors }))] }), !isCameraLoaded && (_jsx("div", { className: "loading", children: _jsx("img", { src: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAYAAAAeP4ixAAAACXBIWXMAAAsTAAALEwEAmpwYAAAEKUlEQVR4nO2aWYicRRCAWzfeBx4xCh4EUbxQV+OFoCPZv+ofowRFVqNRVPBJ8+STL6KiqBAv0HiAT+rTgkI886BsQInZ9a/6J5tFzWpQkGgeJG5m5q+eUZOWyo7h255/1j3mjPvBPP3d1V1d3dXVVWPMIocobmv+RBsDWoInhfBTIShYgl+EIRHGPy3BHiGc0G+W8cUkxtsmx/EU0w244dySCgWrheEjYfjbMrq5/LSPMG4Sgnvd+OCRHVHAMjxsGXfNdfIz/HYJwaNuIn9UW5SwFAwI4fYmKuBb6YcKw82ttQLBC0K4v+EkCLcL4QaJcW05hv5kW/4sF916rPadHLvl5CrlL0sI71A5ljCeQc5+leO2XHdMU5Uoja08XQiGs1cQJ4Xh1Srj5XOVW4nwQkuwXhj2NlBqa6kAy5qoBO5ocFDfLEa5pQsd4484d5JlfOmAd6tfqJ0SBecsaIC9IwOnCuNYlvCEgqtMkynH0C+M32ZstYnyyKoz5iXURSuOsAxbMizxWSv9vyvAccL4YcY2+3peHs0SPlsvDN53Q4N9psW4ocE+y/BOvWXg9TkJsjF4kxDs87bTpnZeWm44t0QYN/rezHIYzE7A0GCfEHzvrcRPGoK0fPYeOqYwfOefz1ktqDDc7ymxTy1kOkR5ygFM92aE6/7zgAvDj545N5gOI4SveGf1V53rTB3u8qxR0dvZdJhilFsqBMVpc4vCOxt2qIXYXWWNRlbRa8A0Qt8M6cbVKLjUdAkVzl/s7ZZqQwdkCTenvMMXpssQ79bXx1lmQw3QNIizBM81LVhrIpbhZe/QP2N6EWG8xzvDn5hepBxDv//+Mb1ISbf+9Pjr9+yoU10cwfoKB+ebLsQ5c5hmZlI3PNc10v2W8lifmy5FovAGSzBiGb8sFwauqGtgCXenzVbk/GmmFxHPR2dq2wsIwcfezbnG9CLC8PghoYgbzh1tGb+pHfaNHUlhNhON89vxLl/kf0tx6lW2RrOAplcpFWCZMNpaGLC7HIVXml7ERvCIF2XucF+tPsH0Ggnnt84oH3xgehFheMNPWVYouKgdYzv3xOFaZ9T6o2UgG4fLF3ZBEoymImLrxnPHmzZgCdd5W/vtBXsurVtoJlxzXqZNWMZ3vTTpWy0bTC2WEK7Q0lqzZVc4WCWMfx0s9Izi2aYVaH1Eiy81s5f0ZTmfwZJ45ZmW8WndOrR04Nz0t2ocXpJEcHvT64hpbAwP1NVOCPboxGYvI1xuGX5ORdvFVlh3RoTgxoyKkhMK7/u3jUbPwvjalMWgaAmemqYI4YP1i4HntVWRAxPh4KH0HwY0hVnZFl5w8HsUhH55Iv3qFMbrtU/Kou+p2zWdYGrVg7s145fEeE36m+7vuhX3zkESwbWW4Xm1pFaoTLeibrN2qf0mjI91ej6LmDbyDybrXV7p+NcrAAAAAElFTkSuQmCC", alt: "loading" }) })), isWaiting && (_jsx("div", { className: "recording-waiting-indicator", children: "Resuming recording\u2026" }))] }));
};
